import '@/app/globals.css';
import { AppProps } from 'next/app';
import { processSeoDataForRegistry, replaceTemplateVariables } from '@/lib/seoHelpers';
import { SeoScriptRegistry } from '@/lib/seoScriptRegistry';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import SWRProvider from '@/providers/SWRProvider';
import { CACHE_KEYS } from '@/lib/swr-config';

const BirdEyeChat = dynamic(() => import('@/components/BirdEyeChat'), { ssr: false });
const ClientSideEffects = dynamic(() => import('@/components/ClientSideEffects'), { ssr: false });
const ScreenDetector = dynamic(() => import('@/utils/screenDetector'), { ssr: false });
const AccessibeScript = dynamic(() => import('@/components/AccessibeScript'), { ssr: false });

function isBlogCategoryOrArchivePage(page: AppProps['pageProps']['page']): boolean {
  const sitePath = page?.sitePath;
  if (!sitePath) return false;

  const blogSlug = process.env.NEXT_PUBLIC_BLOG_SLUG || 'blog';
  const categorySlug = process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG || 'category';
  const archiveSlug = process.env.NEXT_PUBLIC_BLOG_ARCHIVE_SLUG || 'archive';

  return (
    sitePath.includes(`/${blogSlug}/${categorySlug}/`) ||
    sitePath.includes(`/${blogSlug}/${archiveSlug}/`)
  );
}

function App({ Component, pageProps }: AppProps) {
 // Process SEO data on the server side
 if (typeof window === 'undefined') {
  // Always reset registry between requests
  if (pageProps.page?.seo) {
    processSeoDataForRegistry(pageProps.page.seo, pageProps.page, pageProps.page.version === 'staging');
  }
}

// Get the default page title from page data
const defaultTitle = pageProps.page?.title || '';
const isStaging = pageProps.page?.version === 'staging';
const isForApproval = (
  pageProps.page?.status === 'forapproval' ||
  pageProps.page?.patient?.status === 'forapproval' ||
  pageProps.page?.procedure?.status === 'forapproval'
);
const isBlogCategoryOrArchive = isBlogCategoryOrArchivePage(pageProps.page);

// Initialize SEO values
let metaTitle = isStaging ? '(Staging Version) ' + defaultTitle : defaultTitle;
let metaDescription = '';

// Extract SEO values from entries if available
if (pageProps.page?.seo && Array.isArray(pageProps.page.seo)) {
  for (const seoItem of pageProps.page.seo) {
    if (!seoItem.seo_collection_id?.entries || !Array.isArray(seoItem.seo_collection_id?.entries)) {
      continue;
    }
    
    // Search through all entries for both title and description
    for (const entry of seoItem.seo_collection_id.entries) {
      if (!entry.seo_entries_id) continue;
      
      // Handle SEO title
      if (entry.seo_entries_id.type === 'title' || entry.seo_entries_id.type === 'page_title') {
        let titleContent = entry.seo_entries_id.entry || '';
        // Replace any template variables with values from pageData
        titleContent = replaceTemplateVariables(titleContent, pageProps.page, pageProps.page?.seo);
        metaTitle = isStaging ? '(Staging Version) ' + titleContent : titleContent;
      }
      
      // Handle SEO description
      if (entry.seo_entries_id.type === 'description') {
        metaDescription = entry.seo_entries_id.entry || '';
        // Replace any template variables with values from pageData
        metaDescription = replaceTemplateVariables(metaDescription, pageProps.page, pageProps.page?.seo);
      }
    }
  }
}

  const swrFallback = useMemo(() => {
    const fallback: Record<string, unknown> = {};
    if (pageProps.page?.slug) {
      fallback[CACHE_KEYS.page(pageProps.page.slug)] = pageProps.page;
    }
    return fallback;
  }, [pageProps.page]);

  return (
		<SWRProvider fallback={swrFallback}>
			<Head>
				{metaTitle && <title>{metaTitle}</title>}
				{metaDescription && (
					<meta name='description' content={metaDescription} />
				)}
        {isForApproval && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        {!isForApproval && isBlogCategoryOrArchive && (
          <meta name="robots" content="noindex, follow" />
        )}
				<meta name='viewport' content='width=device-width, initial-scale=1' />
				<meta
					name='google-site-verification'
					content='os8kwXT-Y0xwKKF7un1b3dpXINDbDGKRZUgzaq1NG5U'
				/>
			</Head>

			<SeoScriptRegistry.Client />

			<ClientSideEffects />

			<ScreenDetector />
			<Component {...pageProps} />
			<BirdEyeChat />
		</SWRProvider>
	)
}

export default App;
